
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit, Trash2, ArrowUpDown } from "lucide-react";
import { useSpecialties, useDeleteSpecialty, type Specialty } from "@/hooks/useSpecialties";
import AddSpecialtyDialog from "./AddSpecialtyDialog";
import EditSpecialtyDialog from "./EditSpecialtyDialog";

const SpecialtiesManagement = () => {
  const [editingSpecialty, setEditingSpecialty] = useState<Specialty | null>(null);
  const [deletingSpecialty, setDeletingSpecialty] = useState<Specialty | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: specialties, isLoading } = useSpecialties(true); // Include inactive specialties
  const deleteSpecialty = useDeleteSpecialty();

  const handleEdit = (specialty: Specialty) => {
    setEditingSpecialty(specialty);
    setShowEditDialog(true);
  };

  const handleDelete = (specialty: Specialty) => {
    setDeletingSpecialty(specialty);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (deletingSpecialty) {
      deleteSpecialty.mutate(deletingSpecialty.id, {
        onSuccess: () => {
          setShowDeleteDialog(false);
          setDeletingSpecialty(null);
        },
      });
    }
  };

  if (isLoading) {
    return <div>Loading specialties...</div>;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Specialties Management</CardTitle>
          <AddSpecialtyDialog />
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center space-x-2">
                      <span>Order</span>
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {specialties?.map((specialty) => (
                  <TableRow key={specialty.id}>
                    <TableCell className="font-medium">
                      {specialty.display_order}
                    </TableCell>
                    <TableCell className="font-medium">
                      {specialty.name}
                    </TableCell>
                    <TableCell>
                      {specialty.description || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={specialty.is_active ? "default" : "secondary"}
                      >
                        {specialty.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(specialty)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(specialty)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {specialties?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      No specialties found. Add your first specialty to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditSpecialtyDialog
        specialty={editingSpecialty}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Specialty</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingSpecialty?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteSpecialty.isPending}
            >
              {deleteSpecialty.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SpecialtiesManagement;
